/**
 * Gestión → Administración → Boletaje masivo de notas de venta
 * (`/gestion/administracion/boletaje-masivo`).
 *
 * Puerto de `Reportes::sales_note_exchange` + `Reportes::notes_exchange`.
 *
 * Qué se porta:
 * - La **consulta** de notas de venta por liquidar del rango (con producto), que
 *   alimenta la tabla de selección.
 * - El cálculo de la emisión: a partir del **monto seleccionado**, el precio y
 *   las **boletas a generar** se obtienen la **cantidad (GLN/LTR) por boleta** y
 *   el **total por boleta** (igual que `calcular_boletas_calcular`).
 * - La **emisión**: cada boleta crea su venta, su boleta y su línea, y las notas
 *   de origen quedan liquidadas con `BM-<canje>`.
 */

import { FileCheck2, FlaskConical, Search, Ticket } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { nuevaClaveDeEnvio } from '@/lib/clave-de-envio';
import { cn } from '@/lib/utils';

import { AdministracionNav } from '../components/administracion-nav';
import { useBoletaje, useCatalogosBoletas, useNotasBoletaje } from '../administracion-boletas.api';
import type { NotaVentaBoleta } from '../administracion-boletas.types';
import { fechaCorta, hoyIso, hoyMenosDias, monto } from '../gestion-formato';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Redondea como el `round()` del bundle original. */
function redondear(valor: number, decimales: number): number {
  const factor = 10 ** decimales;

  return Math.round((valor + Number.EPSILON) * factor) / factor;
}

/** Primer día de hace dos meses (rango por defecto, como el original). */
function inicioPorDefecto(): string {
  const hoy = new Date();
  hoy.setMonth(hoy.getMonth() - 2);

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`;
}

export function BoletajeMasivoPage() {
  useDocumentTitle('Gestión · Boletaje masivo');

  const catalogos = useCatalogosBoletas();
  const boletaje = useBoletaje();

  // Clave del lote: si el envío se repite (timeout de red), el API no vuelve a emitir las
  // boletas. Se renueva al confirmar.
  const claveDelLote = useRef(nuevaClaveDeEnvio());

  const [desde, setDesde] = useState(inicioPorDefecto);
  const [hasta, setHasta] = useState(() => hoyIso());
  const [producto, setProducto] = useState('');
  const [consultado, setConsultado] = useState(false);
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());

  const [serie, setSerie] = useState('');
  const [fecha, setFecha] = useState(() => hoyIso());
  const [precio, setPrecio] = useState('');
  const [boletas, setBoletas] = useState('');

  const [confirmar, setConfirmar] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const notasQuery = useNotasBoletaje(desde, hasta, producto, consultado);
  const notas = notasQuery.data?.notas ?? [];

  const visibles = notas;
  const clavePaginacion = useMemo(() => [desde, hasta, producto] as const, [desde, hasta, producto]);
  const paginacion = usePaginacion(visibles, { clave: clavePaginacion });

  const seleccionadas = notas.filter((nota) => seleccion.has(nota.id));
  const montoSeleccionado = seleccionadas.reduce((suma, nota) => suma + nota.total, 0);

  // Cálculo de la emisión (calcular_boletas_calcular del original).
  const precioNumero = Number.parseFloat(precio) || 0;
  const boletasNumero = Number.parseInt(boletas, 10) || 0;
  const totalPorBoleta = useMemo(() => {
    if (precioNumero > 0 && boletasNumero > 0 && montoSeleccionado > 0) {
      const cantidad = redondear(montoSeleccionado / boletasNumero / precioNumero, 3);

      return cantidad > 0 ? cantidad : 0;
    }

    return 0;
  }, [precioNumero, boletasNumero, montoSeleccionado]);

  const totalPorBoletaMonto = totalPorBoleta > 0 ? redondear(totalPorBoleta * precioNumero, 2) : 0;

  const productos = catalogos.data?.productos ?? [];
  const series = catalogos.data?.series ?? [];

  const serieEfectiva = serie || series[0] || '';
  const diasMaximos = catalogos.data?.empresa.dias_boleta_masiva ?? 0;

  const consultar = () => {
    setError(null);
    setSeleccion(new Set());
    setConsultado(true);
  };

  const abrirConfirmacion = () => {
    setError(null);
    setConfirmar(true);
  };

  const ejecutar = () => {
    setConfirmar(false);
    setError(null);

    boletaje.mutate(
      {
        producto_id: Number.parseInt(producto, 10),
        precio_producto: precioNumero,
        cantidad_producto: boletasNumero,
        monto: montoSeleccionado,
        total_generar_boleta: totalPorBoleta,
        fecha_generar: fecha,
        empresa_serie: serieEfectiva,
        sale_ids: seleccionadas.map((nota) => nota.id),
        clave_idempotencia: claveDelLote.current,
      },
      {
        onSuccess: (resultado) => {
          claveDelLote.current = nuevaClaveDeEnvio();
          setAviso(resultado.mensaje ?? `Se generaron ${resultado.cantidad_emitido} boletas.`);
          setSeleccion(new Set());
          setConsultado(false);
          void notasQuery.refetch();
        },
        onError: (fallo) => {
          setError(fallo instanceof ApiError ? fallo.message : 'No se pudo generar el boletaje.');
        },
      },
    );
  };

  const listoParaGenerar =
    precioNumero > 0 &&
    boletasNumero > 0 &&
    montoSeleccionado > 0 &&
    totalPorBoleta > 0 &&
    totalPorBoletaMonto > 0 &&
    totalPorBoletaMonto <= 700 &&
    seleccion.size > 0 &&
    serieEfectiva !== '' &&
    fecha !== '';

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-ink-900">Boletaje masivo de notas de venta</h1>
        <p className="mt-1 text-sm text-ink-500">
          Convierte las notas de venta seleccionadas en boletas reales (una venta y un
          comprobante por boleta).
        </p>
      </header>

      <AdministracionNav active="boletaje" />

      {catalogos.data && series.length === 0 ? (
        <Alert variant="info" title="No hay series masivas de boleta">
          Configura una serie con <code>operationType = "masivo"</code> y prefijo <code>B</code> en
          Documentos Fiscales → Series para poder emitir.
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {/* Consulta de notas */}
      <div className="rounded-card border border-ink-100 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
          <FlaskConical className="size-4 text-brand-500" aria-hidden="true" />
          Notas por boletiar
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            label="Fecha inicio"
            type="date"
            value={desde}
            max={hoyIso()}
            onChange={(evento) => setDesde(evento.target.value)}
          />
          <Input
            label="Fecha fin"
            type="date"
            value={hasta}
            max={hoyIso()}
            onChange={(evento) => setHasta(evento.target.value)}
          />
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Producto</span>
            <Select
              className={CONTROL}
              value={producto}
              onChange={(evento) => setProducto(evento.target.value)}
            >
              <option value="">Todos</option>
              {productos.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.nombre}
                </option>
              ))}
            </Select>
          </label>
          <div className="flex items-end">
            <Button
              className="w-full"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              disabled={!desde || !hasta}
              onClick={consultar}
            >
              Consultar
            </Button>
          </div>
        </div>
      </div>

      {notasQuery.isLoading ? <Skeleton className="h-60 w-full" /> : null}

      {notasQuery.isError ? (
        <Alert variant="error" title="No se pudieron leer las notas">
          {notasQuery.error instanceof ApiError ? notasQuery.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {consultado && notasQuery.data ? (
        <div className="rounded-card border border-ink-100 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-ink-500">
              {notas.length} nota(s) · {notasQuery.data.resumen.cantidad.toFixed(3)} GLL ·{' '}
              {monto(notasQuery.data.resumen.importe)} · seleccionadas: {seleccion.size} ·{' '}
              {monto(montoSeleccionado)}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={!paginacion.visibles.length}
                onClick={() => setSeleccion(new Set(paginacion.visibles.map((nota) => nota.id)))}
              >
                Seleccionar página
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!seleccion.size}
                onClick={() => setSeleccion(new Set())}
              >
                Limpiar
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-card border border-ink-100">
            <div className="overflow-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-3 py-2">
                      <input
                        type="checkbox"
                        className="size-4 accent-brand-500"
                        aria-label="Seleccionar todas las notas de la página"
                        checked={
                          paginacion.visibles.length > 0 &&
                          paginacion.visibles.every((nota) => seleccion.has(nota.id))
                        }
                        onChange={(evento) => {
                          if (evento.target.checked)
                            setSeleccion(new Set(paginacion.visibles.map((nota) => nota.id)));
                          else setSeleccion(new Set());
                        }}
                      />
                    </th>
                    <th className="px-3 py-2 whitespace-nowrap">Documento</th>
                    <th className="px-3 py-2 whitespace-nowrap">Fecha</th>
                    <th className="px-3 py-2 whitespace-nowrap">Cliente</th>
                    <th className="px-3 py-2 whitespace-nowrap">Producto</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Cantidad</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Precio</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Total (S/.)</th>
                  </tr>
                </thead>
                <tbody>
                  {paginacion.visibles.map((nota: NotaVentaBoleta) => (
                    <tr
                      key={nota.id}
                      className={cn(
                        'border-t border-ink-100',
                        seleccion.has(nota.id) && 'bg-brand-50/40',
                      )}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          className="size-4 accent-brand-500"
                          aria-label={`Seleccionar la nota ${nota.nota}`}
                          checked={seleccion.has(nota.id)}
                          onChange={() => {
                            setSeleccion((previo) => {
                              const copia = new Set(previo);

                              if (copia.has(nota.id)) copia.delete(nota.id);
                              else copia.add(nota.id);

                              return copia;
                            });
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{nota.nota}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(nota.fecha)}</td>
                      <td className="max-w-[220px] truncate px-3 py-2" title={nota.cliente ?? ''}>
                        {nota.cliente ?? '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{nota.producto ?? '—'}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {nota.cantidad.toFixed(3)}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{monto(nota.precio)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{monto(nota.total)}</td>
                    </tr>
                  ))}

                  {visibles.length === 0 ? (
                    <tr>
                      <td className="px-3 py-6 text-center text-ink-500" colSpan={8}>
                        No hay notas de venta por liquidar en el rango.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="notas" />
          </div>
        </div>
      ) : null}

      {/* Emisión */}
      <div className="rounded-card border border-ink-100 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink-800">
          <FileCheck2 className="size-4 text-brand-500" aria-hidden="true" />
          Emisión
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input label="Tipo comprobante" value="BOLETA" readOnly />
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Serie</span>
            <Select
              className={CONTROL}
              value={serieEfectiva}
              disabled={series.length === 0}
              onChange={(evento) => setSerie(evento.target.value)}
            >
              {series.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </label>
          <Input
            label="Fecha"
            type="date"
            value={fecha}
            min={hoyMenosDias(diasMaximos)}
            max={hoyIso()}
            onChange={(evento) => setFecha(evento.target.value)}
          />
          <Input label="Monto total (S/.) a boletiar" value={montoSeleccionado.toFixed(2)} readOnly />
          <Input
            label="Precio producto"
            type="number"
            min="0"
            max="30"
            step="0.01"
            value={precio}
            onChange={(evento) => setPrecio(evento.target.value)}
          />
          <Input
            label="Boletas x generar"
            type="number"
            min="0"
            max="300"
            step="1"
            value={boletas}
            onChange={(evento) => setBoletas(evento.target.value)}
          />
          <Input label="Cantidad (GLN/LTR) x boleta" value={totalPorBoleta ? totalPorBoleta.toFixed(3) : '0.000'} readOnly />
          <Input
            label="Total (S/.) x boleta"
            value={totalPorBoletaMonto ? totalPorBoletaMonto.toFixed(2) : '0.00'}
            readOnly
          />
        </div>

        {totalPorBoletaMonto > 700 ? (
          <p className="mt-2 text-xs font-medium text-rose-600">
            El total por boleta no debe superar los S/ 700.00. Ajusta el precio o el número de
            boletas.
          </p>
        ) : null}

        <div className="mt-4">
          <Button
            className="w-full sm:w-auto"
            leftIcon={<Ticket className="size-4" aria-hidden="true" />}
            disabled={!listoParaGenerar}
            onClick={abrirConfirmacion}
          >
            Generar las boletas
          </Button>
          {boletaje.isPending ? (
            <Badge variant="warning" className="ml-2">
              Generando…
            </Badge>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirmar}
        title="Generar las boletas"
        description={`Se generarán ${boletasNumero} boletas de S/ ${totalPorBoletaMonto.toFixed(2)} cada una y se liquidarán ${seleccion.size} nota(s). Esta acción no se puede deshacer.`}
        confirmLabel="Sí, generar"
        tone="danger"
        loading={boletaje.isPending}
        onConfirm={ejecutar}
        onClose={() => setConfirmar(false)}
      />
    </div>
  );
}
